import { Upload, List, Button as AntButton } from "antd";
import React, { useState } from "react";
import Button from "../Helpers/Button/Button";
import {
	UploadOutlined,
	CloseOutlined,
	PaperClipOutlined,
} from "@ant-design/icons";
import trans from "../../utils";
import { toast } from "react-toastify";

export default function AntUpload({
	item,
	preview,
	addData,
	setSubmitData,
	submitData,
	readOnly,
	isEditing = false,
	editValues = null,
	view,
}) {
	const [value, setValue] = useState("");
	const rawValue = addData
		? submitData?.find((i) => i.key === item.key)?.value
		: (item?.value ?? value);

	let currentValue;

	if (Array.isArray(rawValue)) {
		currentValue = rawValue;
	} else if (rawValue === "[]" || rawValue == null) {
		currentValue = [];
	} else {
		currentValue = [rawValue];
	}

	// Filter out attachments that are already marked for removal
	const getVisibleValue = () => {
		if (!isEditing || !addData) return currentValue;

		const removedAttachments = addData.values.removed_attachments || [];

		return currentValue.filter((file) => {
			// Check if this is an existing attachment:
			// - Has url/filename/id properties (object), OR
			// - Is a plain string (not a File object) - this means it's from formSubmission.formData
			const isFileObject = file instanceof File;
			const isStringAttachment = typeof file === "string";
			const isObjectAttachment =
				!isFileObject && (file?.url || file?.filename || file?.id);
			const isExistingAttachment = isStringAttachment || isObjectAttachment;

			if (isExistingAttachment) {
				// Handle both string and object formats for identifier
				const fileIdentifier = isStringAttachment
					? file
					: file?.filename || file?.url || file?.name || file?.id;
				const isRemoved = removedAttachments.includes(fileIdentifier);
				return !isRemoved;
			}
			// Newly uploaded files are always visible
			return true;
		});
	};

	const visibleValue = getVisibleValue();
	// Add near the top of your component
	const MAX_FILES = 5;
	const MAX_FILE_SIZE_MB = 5;
	const MAX_TOTAL_MB = 25;

	// Helper to validate files
	const validateFiles = (existingFiles, newFiles) => {
		const combined = [...existingFiles, ...newFiles];

		if (combined.length > MAX_FILES) {
			toast.info(trans(`Maximum ${MAX_FILES || 5} files are allowed.`), {
				autoClose: 800,
				rtl: localStorage.getItem("lang") === "ar",
			});
		}

		for (const file of newFiles) {
			if (file.size / 1024 / 1024 > MAX_FILE_SIZE_MB) {
				toast.info(
					trans(
						`Total file size must not exceed ${MAX_FILE_SIZE_MB / 1024 / 1024}MB.`,
					),
					{ autoClose: 800, rtl: localStorage.getItem("lang") === "ar" },
				);
			}
		}

		const totalMB =
			combined.reduce((sum, f) => sum + (f.size || 0), 0) / 1024 / 1024;
		if (totalMB > MAX_TOTAL_MB) {
			toast.info(
				trans(
					`Total file size must not exceed ${MAX_FILE_SIZE_MB / 1024 / 1024}MB.`,
				),
				{ autoClose: 800, rtl: localStorage.getItem("lang") === "ar" },
			);
		}

		return null; // valid
	};
	return (
		<div style={item?.css}>
			<label
				htmlFor={item?.key}
				className="form-label d-flex align-items-center me-2"
				style={{
					color: item?.css?.color,
					fontFamily: item?.css?.fontFamily,
					fontSize: item?.css?.fontSize,
					fontWeight: item?.css?.fontWeight,
				}}
			>
				<div>{item?.title}</div>
				{item?.required?.value && value === "" && (
					<div className="text-danger ms-2">*</div>
				)}
			</label>
			<Upload
				id={item?.key}
				disabled={readOnly}
				showUploadList={false}
				multiple={true}
				beforeUpload={(file, fileList) => {
					// Only validate on the last file in the batch to avoid duplicate errors
					if (file === fileList[fileList.length - 1]) {
						const existing = visibleValue.filter(
							(f) => f instanceof File || typeof f === "string",
						);
						const error = validateFiles(visibleValue, fileList);
						if (error) {
							return Upload.LIST_IGNORE; // prevents all files from being added
						}
					}
					return false; // prevent auto-upload
				}}
				// ✅ Sync with removed items
				onChange={({ fileList }) => {
					const cleanedFiles = fileList
						.map((f) => f.originFileObj || f)
						.filter(Boolean);
					const error = validateFiles(visibleValue, cleanedFiles);
					if (error) {
						return;
					}
					if (addData) {
						// In edit mode: keep existing attachments (not removed) + new files; otherwise just new files
						const removedAttachments = addData.values.removed_attachments || [];
						const existingKept = (currentValue || []).filter((file) => {
							const isFile = file instanceof File;
							const isExisting =
								typeof file === "string" ||
								(!isFile && (file?.url || file?.filename || file?.id));
							if (!isExisting) return false;
							const id =
								typeof file === "string"
									? file
									: file?.filename || file?.url || file?.name || file?.id;
							return !removedAttachments.includes(id);
						});
						const merged = isEditing
							? [...existingKept, ...cleanedFiles]
							: cleanedFiles;
						setValue(merged);
						setSubmitData((prev) => {
							const filtered = prev?.filter((i) => i.key !== item.key) || [];
							return [
								...filtered,
								{
									key: item.key,
									name: item.name,
									title: item.title,
									value: merged,
								},
							];
						});
						addData.setFieldTouched(`fields[${item.key}].value`, true);
					} else {
						setValue(cleanedFiles);
					}
				}}
			>
				<Button icon={<UploadOutlined />}>{trans("Click to Upload")}</Button>
			</Upload>

			{/* Preview: Shows both existing attachments and newly uploaded files */}
			{((addData && visibleValue?.length > 0) || item?.value?.length > 0) && (
				<div className="mt-3">
					<List
						size="small"
						bordered
						dataSource={visibleValue || item?.value}
						renderItem={(file, idx) => {
							// Check if this is an existing attachment (string or object with url/filename/id)
							const isFileObject = file instanceof File;
							const isStringAttachment = typeof file === "string";
							const isObjectAttachment =
								!isFileObject && (file?.url || file?.filename || file?.id);
							const isExistingAttachment =
								isStringAttachment || isObjectAttachment;

							// Get display name: handle both string and object formats
							const displayName = isStringAttachment
								? file
								: file?.name ||
									file?.filename ||
									file?.original_name ||
									(file?.url ? file.url.split("/").pop() : file);

							// Get file size for description
							const fileSize = file?.size
								? `${(file.size / 1024).toFixed(2)} KB`
								: file?.sizeMB
									? `${file.sizeMB} MB`
									: "";

							// Get file URL if available
							const fileUrl = file?.url || null;

							return (
								<List.Item
									key={idx}
									actions={[
										<AntButton
											type="text"
											danger
											size="small"
											icon={<CloseOutlined />}
											onClick={() => {
												if (isExistingAttachment && isEditing && addData) {
													// For existing attachments, add to removed_attachments array
													const fileIdentifier = isStringAttachment
														? file
														: file?.filename ||
															file?.url ||
															file?.name ||
															file?.id;
													const currentRemoved =
														addData.values.removed_attachments || [];

													if (!currentRemoved.includes(fileIdentifier)) {
														addData.setFieldValue("removed_attachments", [
															...currentRemoved,
															fileIdentifier,
														]);
													}
												} else {
													// For newly uploaded files, remove from currentValue
													const fileIndex = currentValue.findIndex((f) => {
														// Match by reference for File objects, or by name for other objects
														if (f instanceof File && file instanceof File) {
															return f === file;
														}
														return (
															f === file ||
															(f?.name === file?.name &&
																!(f instanceof File) &&
																!(file instanceof File))
														);
													});

													if (fileIndex !== -1) {
														const updated = currentValue.filter(
															(_, index) => index !== fileIndex,
														);

														setSubmitData((prev) => {
															const filtered =
																prev?.filter((i) => i.key !== item.key) || [];
															return [
																...filtered,
																{
																	key: item.key,
																	name: item.name,
																	title: item.title,
																	value: updated,
																},
															];
														});

														setValue(updated);
													}
												}
											}}
											disabled={view}
										>
											{trans("Remove")}
										</AntButton>,
									]}
								>
									<List.Item.Meta
										avatar={<PaperClipOutlined />}
										title={
											fileUrl ? (
												<a
													href={fileUrl}
													target="_blank"
													rel="noopener noreferrer"
												>
													{displayName}
												</a>
											) : (
												<span>{displayName}</span>
											)
										}
										description={fileSize}
									/>
								</List.Item>
							);
						}}
					/>
				</div>
			)}

			{preview !== true ? (
				<>
					{item?.required?.value && visibleValue?.length === 0 && (
						<div className="text-danger mt-1">
							{trans(item?.required?.validationMessage)}
						</div>
					)}
				</>
			) : (
				""
			)}
		</div>
	);
}
